/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useFormState } from 'src/core/hooks/useFormState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { FormValidators } from 'src/core/utils/formValidators';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';

export type VersionCloneModalDto = {
    /**
     * The new version name.
     */
    name: string;
};

const messages = defineMessages({
    defaultTitle: {
        id: 'apps.versionCloneModal.defaultTitle',
        defaultMessage: 'Clone version'
    },
    nameLabel: {
        id: 'apps.versionCloneModal.nameLabel',
        defaultMessage: 'New version name'
    },
    namePlaceholder: {
        id: 'apps.versionCloneModal.namePlaceholder',
        defaultMessage: 'Type version name ...'
    }
});

/**
 * Represents a modal to be used for cloning an app version.
 *
 * @param props The modal properties.
 */
export const VersionCloneModal = withModalMounter((props: BasicModalProps<VersionCloneModalDto>) => {
    const { localize } = useLocalization();
    const { dialogContentProps = {}, onSubmit } = props;

    const title = dialogContentProps.title ?? localize(messages.defaultTitle);
    const { formState, formStateDto, updateFormState } = useFormState<VersionCloneModalDto>({
        name: ['', FormValidators.createRequiredValidator(), FormValidators.createBlacklistValidator()]
    });

    return (
        <BasicModal
            {...props}
            valid={formState.form.valid}
            onSubmit={() => onSubmit(formStateDto)}
            dialogContentProps={{ title, ...dialogContentProps }}>
            <LuisTextField
                required
                autoFocus
                label={localize(messages.nameLabel)}
                placeholder={localize(messages.namePlaceholder)}
                errorMessage={formState.fields.name.errorMessage}
                value={formState.fields.name.value}
                onChange={(_, value) => updateFormState({ name: value })}
                onEnterKeyUp={() => formState.form.valid && onSubmit(formStateDto)}
            />
        </BasicModal>
    );
});
